import { ALL_CENTRES } from '@appTypes/auth';
import { AddOutlined, KeyboardArrowDownOutlined } from '@mui/icons-material';
import { Box, Button, Divider, Menu, MenuItem, Typography } from '@mui/material';
import { useState } from 'react';

import { ManageCentresDialog } from './ManageCentresDialog';
import { ManageMainCentresDialog } from './ManageMainCentresDialog';
import { CHIP_LABEL_SX, CHIP_SX, MENU_PAPER_SX } from './topbar.styles';
import { useScopeSwitcher } from './useScopeSwitcher';

export const ScopeSwitcher = () => {
  const {
    isAdmin,
    isRollup,
    sessions,
    activeMainCentres,
    activeCentres,
    mainCentreId,
    centreId,
    sessionId,
    currentMainCentreName,
    currentCentreName,
    currentSessionLabel,
    changeMainCentre,
    changeCentre,
    changeSession,
  } = useScopeSwitcher();

  const [mainCentreAnchor, setMainCentreAnchor] = useState<null | HTMLElement>(null);
  const [locationAnchor, setLocationAnchor] = useState<null | HTMLElement>(null);
  const [sessionAnchor,  setSessionAnchor]  = useState<null | HTMLElement>(null);
  const [manageCentresOpen, setManageCentresOpen] = useState(false);
  const [manageMainCentresOpen, setManageMainCentresOpen] = useState(false);

  // Staff are pinned to one centre inside one main centre, so both location
  // chips render as static labels for them (no dropdown, no hover affordance).
  const staticChipSx = { cursor: 'default' } as const;

  return (
    <>
      {/* ── Session chip ── */}
      <Box onClick={(e) => setSessionAnchor(e.currentTarget)} sx={CHIP_SX}>
        <Typography sx={CHIP_LABEL_SX}>{currentSessionLabel}</Typography>
        <KeyboardArrowDownOutlined sx={(theme) => ({ fontSize: 12, color: theme.palette.grey[700], flexShrink: 0 })} />
      </Box>
      <Menu
        anchorEl={sessionAnchor}
        open={Boolean(sessionAnchor)}
        onClose={() => setSessionAnchor(null)}
        disableScrollLock
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: { sx: MENU_PAPER_SX } }}
      >
        {/* The ✓ marks what you're VIEWING; the muted "· current" marks the
            live financial year, which becomes 2027-28 on its own each April
            (the server derives isCurrent from the date). The ✓ previously
            marked the current year, which contradicted the row highlight. */}
        {sessions.map((s) => (
          <MenuItem
            key={s._id}
            selected={s._id === sessionId}
            onClick={() => { changeSession(s._id); setSessionAnchor(null); }}
            sx={{ fontSize: '13px', fontWeight: s._id === sessionId ? 600 : 400 }}
          >
            {s.label}
            {s.isCurrent && (
              <Box component="span" sx={(theme) => ({ color: theme.palette.grey[400], fontWeight: 400 })}>
                {'\u00a0·\u00a0current'}
              </Box>
            )}
            {/* Explicit margin rather than a literal space in the string — a
                single space collapses tight against the muted "· current". */}
            {s._id === sessionId && (
              <Box component="span" sx={{ ml: '8px' }}>✓</Box>
            )}
          </MenuItem>
        ))}
        {sessions.length === 0 && (
          <MenuItem disabled sx={{ fontSize: '13px' }}>No sessions</MenuItem>
        )}
      </Menu>

      {/* ── Main Centre chip — the city. Admins switch; staff see it static. ── */}
      <Box
        onClick={isAdmin ? (e) => setMainCentreAnchor(e.currentTarget) : undefined}
        sx={(theme) => ({
          ...CHIP_SX,
          ...(isAdmin ? {} : { ...staticChipSx, '&:hover': { bgcolor: theme.palette.grey[100] } }),
        })}
      >
        <Typography sx={CHIP_LABEL_SX} title={currentMainCentreName}>{currentMainCentreName}</Typography>
        {isAdmin && <KeyboardArrowDownOutlined sx={(theme) => ({ fontSize: 12, color: theme.palette.grey[700], flexShrink: 0 })} />}
      </Box>
      {isAdmin && (
        <Menu
          anchorEl={mainCentreAnchor}
          open={Boolean(mainCentreAnchor)}
          onClose={() => setMainCentreAnchor(null)}
          disableScrollLock
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{ paper: { sx: MENU_PAPER_SX } }}
        >
          {activeMainCentres.map((m) => (
            <MenuItem
              key={m._id}
              selected={m._id === mainCentreId}
              onClick={() => { changeMainCentre(m._id); setMainCentreAnchor(null); }}
              sx={{ fontSize: '13px', fontWeight: m._id === mainCentreId ? 600 : 400 }}
            >
              {m.name}
            </MenuItem>
          ))}
          {activeMainCentres.length === 0 && (
            <MenuItem disabled sx={{ fontSize: '13px' }}>No main centres</MenuItem>
          )}
          <Divider sx={{ my: '4px' }} />
          <Box sx={{ px: '8px', py: '4px' }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => { setMainCentreAnchor(null); setManageMainCentresOpen(true); }}
              startIcon={<AddOutlined sx={{ fontSize: 16 }} />}
              sx={(theme) => ({
                bgcolor: theme.palette.primary.main, borderRadius: '8px', fontSize: 13, fontWeight: 600,
                px: '14px', py: '7px', textTransform: 'none', boxShadow: 'none',
                '&:hover': { bgcolor: theme.palette.primary.dark, boxShadow: 'none' },
              })}
            >
              Manage Main Centres
            </Button>
          </Box>
        </Menu>
      )}

      {/* ── Location chip — centres inside the main centre above. Admins can also
            pick "All Centres" for the read-only rollup; staff are pinned. ── */}
      <Box
        onClick={isAdmin ? (e) => setLocationAnchor(e.currentTarget) : undefined}
        sx={(theme) => ({
          ...CHIP_SX,
          ...(isAdmin ? {} : { ...staticChipSx, '&:hover': { bgcolor: theme.palette.grey[100] } }),
          ...(isRollup ? { bgcolor: theme.palette.status.info.bg } : {}),
        })}
      >
        <Typography sx={CHIP_LABEL_SX} title={currentCentreName}>{currentCentreName}</Typography>
        {isAdmin && <KeyboardArrowDownOutlined sx={(theme) => ({ fontSize: 12, color: theme.palette.grey[700], flexShrink: 0 })} />}
      </Box>
      {isAdmin && (
        <Menu
          anchorEl={locationAnchor}
          open={Boolean(locationAnchor)}
          onClose={() => setLocationAnchor(null)}
          disableScrollLock
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          slotProps={{ paper: { sx: MENU_PAPER_SX } }}
        >
          {activeCentres.map((c) => (
            <MenuItem
              key={c._id}
              selected={c._id === centreId}
              onClick={() => { changeCentre(c._id); setLocationAnchor(null); }}
              sx={{ fontSize: '13px', fontWeight: c._id === centreId ? 600 : 400 }}
            >
              {c.name}
            </MenuItem>
          ))}
          {activeCentres.length === 0 && (
            <MenuItem disabled sx={{ fontSize: '13px' }}>No centres</MenuItem>
          )}

          {/* Read-only combined view — Dashboard and Reports only; adding an
              admission or installment needs a specific centre. */}
          <Divider sx={{ my: '4px' }} />
          <MenuItem
            selected={isRollup}
            disabled={activeCentres.length === 0}
            onClick={() => { changeCentre(ALL_CENTRES); setLocationAnchor(null); }}
            sx={{ fontSize: '13px', fontWeight: isRollup ? 600 : 400, display: 'block', py: '6px' }}
          >
            All Centres
            <Typography sx={(theme) => ({ fontSize: 11, color: theme.palette.grey[500], mt: '1px' })}>
              Dashboard &amp; Reports only
            </Typography>
          </MenuItem>

          <Divider sx={{ my: '4px' }} />
          <Box sx={{ px: '8px', py: '4px' }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => { setLocationAnchor(null); setManageCentresOpen(true); }}
              startIcon={<AddOutlined sx={{ fontSize: 16 }} />}
              sx={(theme) => ({
                bgcolor: theme.palette.primary.main, borderRadius: '8px', fontSize: 13, fontWeight: 600,
                px: '14px', py: '7px', textTransform: 'none', boxShadow: 'none',
                '&:hover': { bgcolor: theme.palette.primary.dark, boxShadow: 'none' },
              })}
            >
              Manage Centres
            </Button>
          </Box>
        </Menu>
      )}

      <ManageMainCentresDialog open={manageMainCentresOpen} onClose={() => setManageMainCentresOpen(false)} />
      <ManageCentresDialog open={manageCentresOpen} onClose={() => setManageCentresOpen(false)} />
    </>
  );
};
